import {
  ResponsiveTable,
  ResponsiveTableBody,
  ResponsiveTableCell,
  ResponsiveTableHead,
  ResponsiveTableHeader,
  ResponsiveTableRow,
} from "@/components/ui/responsive-table";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Event, InvoicePaymentStatus } from "@/pages/admin/events/types";
import { StatusPill, StatusTone } from "@/components/brand/StatusPill";
import {
  ArrowUpDown,
  CalendarDays,
  Clock,
  MapPin,
  Users,
  Dices,
} from "lucide-react";

const PAYMENT_PILLS: Record<
  InvoicePaymentStatus,
  { label: string; tone: StatusTone }
> = {
  PAID: { label: "Fully Paid", tone: "success" },
  PARTIALLY_PAID: { label: "Half Paid", tone: "warning" },
  UNPAID: { label: "Unpaid", tone: "muted" },
  NOT_INVOICED: { label: "Not invoiced", tone: "muted" },
};

interface EventsTableProps {
  events?: Event[];
  onSortColumn: (column: keyof Event) => void;
  sortColumn: keyof Event | null;
  sortDirection: "asc" | "desc";
}

export function EventsTable({
  events,
  onSortColumn,
  sortColumn,
  sortDirection,
}: EventsTableProps) {
  const getSortIcon = (column: keyof Event) => {
    return (
      <Button
        variant="ghost"
        size="sm"
        className="ml-1 p-0 h-auto"
        onClick={() => onSortColumn(column)}
      >
        <ArrowUpDown
          className={`h-4 w-4 ${
            sortColumn === column ? "text-foreground" : "text-foreground"
          }`}
        />
        <span className="sr-only">Sort by {String(column)}</span>
      </Button>
    );
  };

  return (
    <ResponsiveTable className="text-muted-foreground">
      <ResponsiveTableHeader>
        <ResponsiveTableRow>
          <ResponsiveTableHead className="cursor-pointer">
            Event Title {getSortIcon("title")}
          </ResponsiveTableHead>
          <ResponsiveTableHead className="cursor-pointer">
            Date {getSortIcon("date")}
          </ResponsiveTableHead>
          <ResponsiveTableHead className="cursor-pointer">
            Location {getSortIcon("location")}
          </ResponsiveTableHead>
          <ResponsiveTableHead className="cursor-pointer">
            Staff {getSortIcon("staffAssigned")}
          </ResponsiveTableHead>
          <ResponsiveTableHead className="cursor-pointer">
            Attendees {getSortIcon("attendees")}
          </ResponsiveTableHead>
          <ResponsiveTableHead className="cursor-pointer">
            Tables {getSortIcon("filledTables")}
          </ResponsiveTableHead>
          <ResponsiveTableHead>Payment</ResponsiveTableHead>
          <ResponsiveTableHead className="text-right">
            Actions
          </ResponsiveTableHead>
        </ResponsiveTableRow>
      </ResponsiveTableHeader>
      <ResponsiveTableBody>
        {events?.map((event) => (
          <ResponsiveTableRow key={event.id} mobileTitle={event.title}>
            <ResponsiveTableCell
              className="font-medium"
              mobileLabel="Event Title"
              mobilePriority="high"
            >
              {event.title}
            </ResponsiveTableCell>
            <ResponsiveTableCell
              mobileLabel="Date & Time"
              mobilePriority="medium"
            >
              <div className="flex flex-col space-y-1">
                <div className="flex items-center gap-1">
                  <CalendarDays className="w-4 h-4" />
                  <span>{event.date}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  <span>{event.time}</span>
                </div>
              </div>
            </ResponsiveTableCell>
            <ResponsiveTableCell mobileLabel="Location" mobilePriority="medium">
              <div className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                <span>{event.location}</span>
              </div>
            </ResponsiveTableCell>
            <ResponsiveTableCell
              mobileLabel="Staff Status"
              mobilePriority="medium"
            >
              <div className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                <span>
                  {event.staffAssigned}/{event.staffNeeded}
                </span>
              </div>
            </ResponsiveTableCell>
            <ResponsiveTableCell mobileLabel="Attendees" mobilePriority="low">
              <div className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                <span>{event.attendees}</span>
              </div>
            </ResponsiveTableCell>
            <ResponsiveTableCell
              mobileLabel="Tables Status"
              mobilePriority="low"
            >
              <div className="flex items-center gap-1">
                <Dices className="w-4 h-4" />
                <span>
                  {event.filledTables}/{event.totalTables}
                </span>
              </div>
            </ResponsiveTableCell>
            <ResponsiveTableCell mobileLabel="Payment" mobilePriority="medium">
              <PaymentPill status={event.paymentStatus} />
            </ResponsiveTableCell>
            <ResponsiveTableCell className="text-right">
              <Link to={`/admin/events/${event.id}`}>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full sm:w-auto"
                >
                  View Details
                </Button>
              </Link>
            </ResponsiveTableCell>
          </ResponsiveTableRow>
        ))}
        {(!events || events.length === 0) && (
          <ResponsiveTableRow>
            <ResponsiveTableCell
              colSpan={8}
              className="text-center py-6 text-muted-foreground"
            >
              No events found
            </ResponsiveTableCell>
          </ResponsiveTableRow>
        )}
      </ResponsiveTableBody>
    </ResponsiveTable>
  );
}

function PaymentPill({ status }: { status: InvoicePaymentStatus }) {
  const pill = PAYMENT_PILLS[status];
  if (!pill) return null;
  return <StatusPill tone={pill.tone}>{pill.label}</StatusPill>;
}
